import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { AccessibleLoader } from '@/components/shared/AccessibleLoader';
import BuilderEditorPage from '@/pages/BuilderEditorPage';
import DeckEditorPage from '@/pages/DeckEditorPage';
import { useLocalizedNavigate } from '@/hooks/useLocalizedNavigate';
import { builderApi, type BuildKind } from '@/utils/api-pb';

/**
 * Picks the editor for `/builder/:buildId`.
 *
 * A landing page and a deck share one list, one id space and one set of API routes,
 * and the only thing that says which editor to open is `build.kind` — which lives on
 * the server. So the route asks first and then mounts one editor or the other, rather
 * than the editors growing a second mode each and branching all the way down.
 */
export default function BuilderEditorRoute() {
  const { buildId = '' } = useParams();
  const { t } = useTranslation('builder');
  const navigate = useLocalizedNavigate();
  const [kind, setKind] = useState<BuildKind | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const data = await builderApi.get(buildId);
        if (!cancelled) setKind(data.build.kind);
      } catch (error) {
        if (cancelled) return;
        toast.error(error instanceof Error ? error.message : t('toast.error'));
        navigate('/builder');
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [buildId, navigate, t]);

  if (!kind) return <AccessibleLoader label={t('loading', { ns: 'common' })} />;

  return kind === 'deck' ? <DeckEditorPage /> : <BuilderEditorPage />;
}
